// "Fuer alle Haeuser freigeben" im Kontaktkopf (ADR-0064 Ziffer 4/5 mit
// Nachtrag S4a, ST-SEC-001 S4b).
//
// KLARE HAUPTAKTION (NFR-UX-004): kein Knopf im Kopf. Der Eintrag steht im
// Menue "Weitere Aktionen" (kopf.tsx); erst die Rueckfrage hier hat einen
// primaeren Knopf, und der gilt nur im Dialog.
//
// INFORMIERTE ENTSCHEIDUNG (CM-UX-004): der Dialog sagt, was alle Haeuser
// danach sehen, was beim Haus bleibt und dass die Freigabe endgueltig ist. Der
// Fokus steht beim Oeffnen auf "Abbrechen" (APG Alert Dialog: nicht
// umkehrbare Aktion).
//
// DIE STATUSREGION "Status: Freigabe" ist immer im DOM, auch bei
// geschlossenem Dialog, damit sie Ergebnisse ansagt; leer ist sie aus dem
// Fluss (sonst 16 px Abstand als Flex-Kind im ersten Bildschirm). Sie traegt
// nur Ergebnisse dieses Befehls; Abbrechen und Esc schreiben nichts. Nach der
// Antwort geht der Fokus dorthin, weil der Menueeintrag danach fehlt.
//
// Der Dialog ist nur eingehaengt, wenn er offen ist: Fluents Fokuswaechter
// stuenden sonst auch geschlossen im Body (`aria-hidden-focus`, siehe
// stichworte.tsx).

import { useEffect, useRef, type ReactElement, type RefObject } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';

import { fetchParty, type PartyDetail } from '../../parties.ts';
import { PARTY_CONFIRM_MESSAGES, partyLockVersion } from '../../party-lifecycle.ts';
import { ALREADY_SHARED, SHARE_BUTTON, SHARE_TITLE, shareConfirmation, shareDialogText, shareParty } from '../../party-share.ts';
import { useSectionCommands, meldungsIntent } from '../../shell/use-section-commands.ts';
import { NebenKnopf } from '../../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  // Wie `verborgen` in status-aktion.tsx: im DOM, fuer Screenreader da, ohne Platz.
  verborgen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  absaetze: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  // Den Abstand traegt allein `gap`; die Standardraender der p kaemen dazu.
  absatz: {
    margin: 0,
  },
});

const INHALT_ID = 'freigabe-dialog-inhalt';

export function FreigabeAktion({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  party,
  onParty,
  offen,
  onOffen,
  ausloeserRef,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly party: PartyDetail;
  readonly onParty: (party: PartyDetail) => void;
  /** Der Dialog wird ueber den Menueeintrag im Kopf geoeffnet. */
  readonly offen: boolean;
  readonly onOffen: (offen: boolean) => void;
  /** "Weitere Aktionen": dorthin geht der Fokus nach Abbrechen und Esc. */
  readonly ausloeserRef: RefObject<HTMLButtonElement | null>;
}): ReactElement {
  const own = useStyles();
  const abbrechenRef = useRef<HTMLButtonElement | null>(null);
  // Eine spaete Antwort nach dem Wechsel zu einem anderen Kontakt (die Seite
  // bleibt stehen, diese Aktion nicht: key je Party) darf ihn nicht mit dieser
  // Party ueberschreiben.
  const eingehaengt = useRef(true);
  useEffect(() => {
    eingehaengt.current = true;
    return () => {
      eingehaengt.current = false;
    };
  }, []);
  const uebernehmen = (neu: PartyDetail): void => {
    if (eingehaengt.current) onParty(neu);
  };
  const haus = party.visibility?.ownerHouseName ?? null;
  const art = party.visibility?.ownerHouseKind ?? null;
  const text = shareDialogText(haus, art);

  const befehle = useSectionCommands({
    accessToken,
    onReauthenticate,
    // Nach Konflikt oder 404: den jetzt gueltigen Stand zeigen.
    refresh: async () => {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return undefined;
      }
      const result = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId: party.partyId });
      if (result.outcome === 'loaded') {
        uebernehmen(result.party);
        return undefined;
      }
      return result.outcome === 'rejected' ? result.message : PARTY_CONFIRM_MESSAGES.notFound;
    },
  });
  const { message, messageOffen, confirmation: bestaetigung, busy, statusRef, clearMessages } = befehle;

  useEffect(() => {
    if (!offen) {
      return undefined;
    }
    // Ein Satz aus dem letzten Versuch gehoert nicht zur neuen Rueckfrage;
    // leer wird nichts angesagt.
    clearMessages();
    // Fluents Menue gibt den Fokus beim Schliessen an den Ausloeser zurueck;
    // erst danach auf "Abbrechen", sonst gewinnt der Ausloeser.
    const zeiger = window.setTimeout(() => abbrechenRef.current?.focus(), 0);
    return () => window.clearTimeout(zeiger);
  }, [offen, clearMessages]);

  const schliessen = (): void => {
    onOffen(false);
    window.setTimeout(() => ausloeserRef.current?.focus(), 0);
  };

  const freigeben = (): void => {
    if (busy) {
      return;
    }
    clearMessages();
    void befehle.command({
      run: async (token) => {
        // Der Dialog schliesst bei jeder Antwort UND bei einem Abbruch: die
        // Meldung steht in der Region, und hinter einem modalen Dialog waere
        // sie fuer Hilfsmittel verborgen.
        try {
          return await shareParty({
            baseUrl: apiBaseUrl,
            accessToken: token,
            partyId: party.partyId,
            lockVersion: partyLockVersion(party),
            ownerHouseName: haus,
            ownerHouseKind: art,
          });
        } finally {
          onOffen(false);
        }
      },
      confirm: (applied) => shareConfirmation(applied, haus, art),
      // Die Antwort traegt Stand und Klasse; der Rest der Party ist unveraendert.
      reload: false,
      onApplied: (applied) => {
        uebernehmen({
          ...party,
          lockVersion: applied.lockVersion,
          ...(party.person === undefined ? {} : { person: { ...party.person, lockVersion: applied.lockVersion } }),
          visibility: { scope: 'groupwide', ownerHouseName: null },
        });
      },
    });
  };

  const mitInhalt = bestaetigung !== undefined || message !== undefined;

  return (
    <>
      <div data-freigabe className={mitInhalt ? undefined : own.verborgen}>
        <div ref={statusRef} tabIndex={-1} role="status" aria-live="polite" aria-label="Status: Freigabe">
          {bestaetigung !== undefined ? (
            <MessageBar intent={bestaetigung === ALREADY_SHARED ? 'info' : 'success'}>
              <MessageBarBody>
                <MessageBarTitle>{bestaetigung === ALREADY_SHARED ? 'Nichts geändert' : 'Freigegeben'}</MessageBarTitle>
                <div>{bestaetigung}</div>
              </MessageBarBody>
            </MessageBar>
          ) : undefined}
          {message !== undefined ? (
            <MessageBar intent={meldungsIntent(message, messageOffen)}>
              <MessageBarBody>
                <MessageBarTitle>{message.headline}</MessageBarTitle>
                <div>{message.explanation}</div>
                <div>{message.nextStep}</div>
              </MessageBarBody>
            </MessageBar>
          ) : undefined}
        </div>
      </div>
      {offen ? (
        <Dialog
          open
          modalType="alert"
          onOpenChange={(_event, data) => {
            if (!data.open && !busy) schliessen();
          }}
        >
          {/* Fluent setzt keine Beschreibung; der Inhalt ist die Entscheidungsgrundlage. */}
          <DialogSurface aria-describedby={INHALT_ID}>
            <DialogBody>
              <DialogTitle>{SHARE_TITLE}</DialogTitle>
              <DialogContent id={INHALT_ID} className={own.absaetze}>
                <Body1 as="p" className={own.absatz}>{text.sichtbar}</Body1>
                <Body1 as="p" className={own.absatz}>{text.bleibt}</Body1>
                {/* Der Satz traegt, nicht die Farbe (NFR-ACC-001, 1.4.1). */}
                <Body1Strong as="p" className={own.absatz}>{text.endgueltig}</Body1Strong>
              </DialogContent>
              <DialogActions>
                <NebenKnopf
                  ref={(knoten: HTMLButtonElement | null) => {
                    abbrechenRef.current = knoten;
                  }}
                  disabled={busy}
                  onClick={schliessen}
                >
                  Abbrechen
                </NebenKnopf>
                <Button appearance="primary" disabled={busy} onClick={freigeben}>
                  {SHARE_BUTTON}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      ) : undefined}
    </>
  );
}
